import axios from 'axios'
import {
	call,
	takeEvery,
	takeLatest,
	put,
	all,
	select,
	delay,
} from 'redux-saga/effects'
import { startSubmit, stopSubmit, reset } from 'redux-form'

import { articleActions } from 'features/articles'
import { settingsTypes, settingsActions } from '.'
import { authActions, setTheme, setScheme } from '../auth'

const getCurrentArticleId = state => state.articles.article.id
const getArticleCreatedBy = state => state.articles.article.createdBy
const getArticleUpdatedBy = state => state.articles.article.updatedBy

function* getProfileRequest() {
	try {
		const { data } = yield call(axios.get, 'profile')

		yield put(settingsActions.getProfileSuccess(data))
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('profile', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('profile', { _error: error.response.data.message })
			)
		}
	}
}

function* updateProfileRequest({ profile }) {
	try {
		yield put(startSubmit('profile'))
		const { data } = yield call(axios.post, 'profile', profile)

		// Put loginSuccess for refreshing auth object used in initial
		yield all([
			put(settingsActions.updateProfileSuccess(data)),
			put(
				authActions.loginSuccess({
					firstName: data.firstName,
					lastName: data.lastName,
				})
			),
		])

		const articleCreatedBy = yield select(getArticleCreatedBy)
		const articleUpdatedBy = yield select(getArticleUpdatedBy)

		if (articleCreatedBy.id === data.id) {
			articleCreatedBy.firstName = data.firstName
			articleCreatedBy.lastName = data.lastName
		}

		if (articleUpdatedBy && articleUpdatedBy.id === data.id) {
			articleUpdatedBy.firstName = data.firstName
			articleUpdatedBy.lastName = data.lastName
		}

		yield put(
			articleActions.updateArticleMeta({
				createdBy: articleCreatedBy,
				updatedBy: articleUpdatedBy,
			})
		)

		yield delay(1000)
		yield put(stopSubmit('profile'))
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('profile', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('profile', { _error: error.response.data.message })
			)
		}
	}
}

function* updateThemeRequest({ settings }) {
	try {
		const { data } = yield call(axios.post, 'profile/theme', settings)

		yield all([
			call(setTheme, settings.theme),
			put(settingsActions.updateProfileSuccess(data)),
			put(authActions.loginSuccess(data)),
		])
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('profile', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('profile', { _error: error.response.data.message })
			)
		}
	}
}

function* updateSchemeRequest({ scheme }) {
	try {
		const { data } = yield call(axios.post, 'team', { colorScheme: scheme })

		yield all([
			call(setScheme, scheme),
			put(authActions.loginSuccess({ team: { ...data } })),
		])
	} catch (error) {
		//
	}
}

function* updateReportSettingsRequest({ settings }) {
	try {
		const { data } = yield call(axios.post, 'profile/reports', settings)

		yield all([
			put(settingsActions.updateProfileSuccess(data)),
			put(authActions.loginSuccess(data)),
		])
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('profile', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('profile', { _error: error.response.data.message })
			)
		}
	}
}

function* updatePasswordRequest({ passwords }) {
	try {
		yield call(axios.post, 'profile/password', {
			currentPassword: passwords.currentPassword,
			newPassword: passwords.newPassword,
		})
		yield put(reset('changePassword'))
		yield call(passwords.callback)
	} catch (error) {
		if (!error.response) {
			yield put(
				stopSubmit('changePassword', { _error: 'connection_failed' })
			)
		} else if (error.response.status === 422) {
			yield put(
				stopSubmit('changePassword', {
					currentPassword: error.response.data.message,
				})
			)
		} else if (error.response.status === 411) {
			yield put(
				stopSubmit('changePassword', {
					newPassword: error.response.data.message,
				})
			)
		} else {
			yield put(
				stopSubmit('changePassword', {
					_error: error.response.data.message,
				})
			)
		}
	}
}

const jumpToTeamSettings = (slug, currentArticleId) => {
	window.location.href = `/${slug}/${currentArticleId}#settings-team`
}

function* updateSlugRequest({ slug, password }) {
	try {
		const response = yield call(axios.post, 'team/slug', {
			slug,
			password,
		})

		const currentArticleId = yield select(getCurrentArticleId)

		yield call(jumpToTeamSettings, response.data.slug, currentArticleId)
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('changeSlug', { _error: 'connection_failed' }))
		} else if (error.response.status === 401) {
			yield put(
				stopSubmit('changeSlug', {
					password: error.response.data.message,
				})
			)
		} else if (error.response.status === 409) {
			yield put(
				stopSubmit('changeSlug', {
					teamUrl: error.response.data.message,
				})
			)
		} else {
			yield put(
				stopSubmit('changeSlug', {
					_error: error.response.data.message,
				})
			)
		}
	}
}

function* getTeamRequest() {
	try {
		const invites = yield call(axios.get, 'team/invites')
		const team = yield call(axios.get, 'team')

		yield all([
			put(settingsActions.getInvitesSuccess(invites.data)),
			put(settingsActions.getTeamSuccess(team.data)),
		])
	} catch (error) {
		// yield put(stopSubmit('profile', { _error: error.response.data.message }))
	}
}

function* getAchievementsRequest() {
	try {
		const achievements = yield call(axios.get, 'profile/achievements')

		yield put(settingsActions.getAchievementsSuccess(achievements.data))
	} catch (error) {
		// yield put(stopSubmit('profile', { _error: error.response.data.message }))
	}
}

function* updateTeamNameRequest({ teamName }) {
	try {
		const { data } = yield call(axios.post, 'team', { name: teamName })

		yield put(authActions.loginSuccess({ team: { ...data } }))
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('team', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('team', { _error: error.response.data.message })
			)
		}
	}
}

function* updateTeamMemberRequest({ member }) {
	try {
		const { data } = yield call(axios.post, `team/${member.id}`, {
			role: member.role,
			isActive: member.isActive,
		})

		yield put(settingsActions.updateTeamMemberSuccess(data))
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('team', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('team', { _error: error.response.data.message })
			)
		}
	}
}

function* removeTeamMemberRequest({ member }) {
	try {
		yield call(axios.delete, `team/${member}`)
		yield put(settingsActions.removeTeamMemberSuccess(member))
	} catch (error) {
		if (!error.response) {
			yield put(stopSubmit('team', { _error: 'connection_failed' }))
		} else {
			yield put(
				stopSubmit('team', { _error: error.response.data.message })
			)
		}
	}
}

function* createInvitesRequest({ invites }) {
	yield put(startSubmit('invite'))

	try {
		yield call(axios.post, 'team/invites', invites)

		yield all([
			put(reset('invite')),
			put(settingsActions.getInvitesRequest()),
			put(stopSubmit('invite')),
		])
	} catch (error) {
		yield put(stopSubmit('invite'))
		// yield put(stopSubmit('invite', { _error: error.response.data.message }))
	}
}

function* getInvitesRequest() {
	try {
		const { data } = yield call(axios.get, 'team/invites')

		yield put(settingsActions.getInvitesSuccess(data))
	} catch (error) {
		//
	}
}

function* removeInviteRequest({ invite }) {
	try {
		yield call(axios.delete, `team/invites/${invite}`)
		yield put(settingsActions.removeInviteSuccess(invite))
	} catch (error) {
		// yield put(stopSubmit('invite', { _error: error.response.data.message }))
	}
}

function* reinviteRequest({ invite }) {
	try {
		yield call(axios.get, `team/invites/${invite}/reinvite`)
	} catch (error) {
		//
	}
}

function* updateUserImageRequest({ image }) {
	try {
		const formData = new FormData()
		formData.append('file', image)

		const { data } = yield call(
			axios.post,
			`profile/user-image`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)

		yield all([
			put(settingsActions.updateUserImageSuccess(data)),
			put(authActions.loginSuccess({ image: data.image })),
		])

		const articleCreatedBy = yield select(getArticleCreatedBy)
		const articleUpdatedBy = yield select(getArticleUpdatedBy)

		if (articleCreatedBy.id === data.id) {
			articleCreatedBy.image = data.image
		}

		if (articleUpdatedBy && articleUpdatedBy.id === data.id) {
			articleUpdatedBy.image = data.image
		}

		yield put(
			articleActions.updateArticleMeta({
				createdBy: articleCreatedBy,
				updatedBy: articleUpdatedBy,
			})
		)
	} catch (error) {
		//
	}
}

function* updateTeamImageRequest({ image }) {
	try {
		const formData = new FormData()
		formData.append('file', image)

		const { data } = yield call(axios.post, `team/team-image`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})

		yield all([
			put(
				authActions.loginSuccess({
					team: {
						image: data.image,
					},
				})
			),
			put(settingsActions.updateTeamImageSuccess()),
		])
	} catch (error) {
		//
	}
}

function* updateTeamLogoRequest({ image }) {
	try {
		const formData = new FormData()

		formData.append('type', 'logo')
		formData.append('file', image)

		const { data } = yield call(axios.post, `team/assets`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})

		yield all([
			put(
				authActions.loginSuccess({
					team: {
						logo: data.logo,
						logoRatio: data.logoRatio,
					},
				})
			),
			put(settingsActions.updateTeamLogoSuccess()),
		])
	} catch (error) {
		//
	}
}

function* updateTeamLogoNegativeRequest({ image }) {
	try {
		const formData = new FormData()

		formData.append('type', 'logo-negative')
		formData.append('file', image)

		const { data } = yield call(axios.post, `team/assets`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})

		yield all([
			put(
				authActions.loginSuccess({
					team: {
						logoNegative: data.logoNegative,
						logoNegativeRatio: data.logoNegativeRatio,
					},
				})
			),
			put(settingsActions.updateTeamLogoNegativeSuccess()),
		])
	} catch (error) {
		//
	}
}

function* updateTeamFaviconRequest({ image }) {
	try {
		const formData = new FormData()

		formData.append('type', 'favicon')
		formData.append('file', image)

		const { data } = yield call(axios.post, `team/assets`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})

		yield all([
			put(
				authActions.loginSuccess({
					team: {
						favicon:
							data.favicon +
							'?' +
							Math.random()
								.toString(36)
								.slice(-5),
					},
				})
			),
			put(settingsActions.updateTeamFaviconSuccess()),
		])
	} catch (error) {
		//
	}
}

const settingsSagas = [
	takeEvery(settingsTypes.GET_PROFILE_REQUEST, getProfileRequest),
	takeLatest(settingsTypes.UPDATE_PROFILE_REQUEST, updateProfileRequest),
	takeLatest(settingsTypes.UPDATE_PASSWORD_REQUEST, updatePasswordRequest),
	takeLatest(settingsTypes.UPDATE_SLUG_REQUEST, updateSlugRequest),
	takeEvery(settingsTypes.GET_ACHIEVEMENTS_REQUEST, getAchievementsRequest),
	takeEvery(settingsTypes.GET_TEAM_REQUEST, getTeamRequest),
	takeLatest(settingsTypes.UPDATE_TEAM_NAME_REQUEST, updateTeamNameRequest),
	takeEvery(
		settingsTypes.UPDATE_TEAM_MEMBER_REQUEST,
		updateTeamMemberRequest
	),
	takeEvery(
		settingsTypes.REMOVE_TEAM_MEMBER_REQUEST,
		removeTeamMemberRequest
	),
	takeEvery(settingsTypes.CREATE_INVITES_REQUEST, createInvitesRequest),
	takeEvery(settingsTypes.GET_INVITES_REQUEST, getInvitesRequest),
	takeEvery(settingsTypes.REMOVE_INVITE_REQUEST, removeInviteRequest),
	takeEvery(settingsTypes.REINVITE_REQUEST, reinviteRequest),
	takeEvery(settingsTypes.UPDATE_THEME_REQUEST, updateThemeRequest),
	takeEvery(
		settingsTypes.UPDATE_REPORT_SETTINGS_REQUEST,
		updateReportSettingsRequest
	),
	takeEvery(settingsTypes.UPDATE_USER_IMAGE_REQUEST, updateUserImageRequest),
	takeEvery(settingsTypes.UPDATE_TEAM_IMAGE_REQUEST, updateTeamImageRequest),
	takeEvery(settingsTypes.UPDATE_TEAM_LOGO_REQUEST, updateTeamLogoRequest),
	takeEvery(
		settingsTypes.UPDATE_TEAM_LOGO_NEGATIVE_REQUEST,
		updateTeamLogoNegativeRequest
	),
	takeEvery(
		settingsTypes.UPDATE_TEAM_FAVICON_REQUEST,
		updateTeamFaviconRequest
	),
	takeEvery(settingsTypes.UPDATE_SCHEME_REQUEST, updateSchemeRequest),
]

export default settingsSagas
